import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import AsyncSelect from 'react-select/async';
import { 
  Calendar, 
  MapPin, 
  User, 
  Clock, 
  ArrowRight, 
  Save,
  Loader2,
  ChevronDown,
  Search,
  Info
} from 'lucide-react';
import "bulma/css/bulma.min.css";
import '../../assets/ascendant-display.sass';

// Major cities with their coordinates - same as birth chart form
const majorCities = [
  { name: "New York, NY, USA", lat: 40.7128, lon: -74.0060 },
  { name: "Los Angeles, CA, USA", lat: 34.0522, lon: -118.2437 },
  { name: "Chicago, IL, USA", lat: 41.8781, lon: -87.6298 },
  { name: "Houston, TX, USA", lat: 29.7604, lon: -95.3698 },
  { name: "Phoenix, AZ, USA", lat: 33.4484, lon: -112.0740 },
  { name: "Philadelphia, PA, USA", lat: 39.9526, lon: -75.1652 },
  { name: "San Antonio, TX, USA", lat: 29.4241, lon: -98.4936 },
  { name: "San Diego, CA, USA", lat: 32.7157, lon: -117.1611 },
  { name: "Dallas, TX, USA", lat: 32.7767, lon: -96.7970 },
  { name: "San Jose, CA, USA", lat: 37.3382, lon: -121.8863 },
  { name: "London, UK", lat: 51.5074, lon: -0.1278 },
  { name: "Paris, France", lat: 48.8566, lon: 2.3522 },
  { name: "Berlin, Germany", lat: 52.5200, lon: 13.4050 },
  { name: "Madrid, Spain", lat: 40.4168, lon: -3.7038 },
  { name: "Rome, Italy", lat: 41.9028, lon: 12.4964 },
  { name: "Amsterdam, Netherlands", lat: 52.3676, lon: 4.9041 },
  { name: "Vienna, Austria", lat: 48.2082, lon: 16.3738 },
  { name: "Prague, Czech Republic", lat: 50.0755, lon: 14.4378 },
  { name: "Budapest, Hungary", lat: 47.4979, lon: 19.0402 },
  { name: "Warsaw, Poland", lat: 52.2297, lon: 21.0122 },
  { name: "Moscow, Russia", lat: 55.7558, lon: 37.6176 },
  { name: "Tokyo, Japan", lat: 35.6762, lon: 139.6503 },
  { name: "Seoul, South Korea", lat: 37.5665, lon: 126.9780 },
  { name: "Beijing, China", lat: 39.9042, lon: 116.4074 },
  { name: "Shanghai, China", lat: 31.2304, lon: 121.4737 },
  { name: "Hong Kong", lat: 22.3193, lon: 114.1694 },
  { name: "Singapore", lat: 1.3521, lon: 103.8198 },
  { name: "Bangkok, Thailand", lat: 13.7563, lon: 100.5018 },
  { name: "Jakarta, Indonesia", lat: -6.2088, lon: 106.8456 },
  { name: "Kuala Lumpur, Malaysia", lat: 3.1390, lon: 101.6869 },
  { name: "Manila, Philippines", lat: 14.5995, lon: 120.9842 },
  { name: "Ho Chi Minh City, Vietnam", lat: 10.8231, lon: 106.6297 },
  { name: "Mumbai, India", lat: 19.0760, lon: 72.8777 },
  { name: "Delhi, India", lat: 28.7041, lon: 77.1025 },
  { name: "Bangalore, India", lat: 12.9716, lon: 77.5946 },
  { name: "Chennai, India", lat: 13.0827, lon: 80.2707 },
  { name: "Kolkata, India", lat: 22.5726, lon: 88.3639 },
  { name: "Hyderabad, India", lat: 17.3850, lon: 78.4867 },
  { name: "Pune, India", lat: 18.5204, lon: 73.8567 },
  { name: "Ahmedabad, India", lat: 23.0225, lon: 72.5714 },
  { name: "Sydney, Australia", lat: -33.8688, lon: 151.2093 },
  { name: "Melbourne, Australia", lat: -37.8136, lon: 144.9631 },
  { name: "Brisbane, Australia", lat: -27.4698, lon: 153.0251 },
  { name: "Perth, Australia", lat: -31.9505, lon: 115.8605 },
  { name: "Adelaide, Australia", lat: -34.9285, lon: 138.6007 },
  { name: "Toronto, Canada", lat: 43.6532, lon: -79.3832 },
  { name: "Montreal, Canada", lat: 45.5017, lon: -73.5673 },
  { name: "Vancouver, Canada", lat: 49.2827, lon: -123.1207 },
  { name: "Calgary, Canada", lat: 51.0447, lon: -114.0719 },
  { name: "Edmonton, Canada", lat: 53.5461, lon: -113.4938 },
  { name: "Ottawa, Canada", lat: 45.4215, lon: -75.6972 },
  { name: "Mexico City, Mexico", lat: 19.4326, lon: -99.1332 },
  { name: "Guadalajara, Mexico", lat: 20.6597, lon: -103.3496 },
  { name: "Monterrey, Mexico", lat: 25.6866, lon: -100.3161 },
  { name: "Puebla, Mexico", lat: 19.0413, lon: -98.2062 },
  { name: "São Paulo, Brazil", lat: -23.5505, lon: -46.6333 },
  { name: "Rio de Janeiro, Brazil", lat: -22.9068, lon: -43.1729 },
  { name: "Brasília, Brazil", lat: -15.7942, lon: -47.8822 },
  { name: "Salvador, Brazil", lat: -12.9714, lon: -38.5011 },
  { name: "Fortaleza, Brazil", lat: -3.7319, lon: -38.5267 },
  { name: "Belo Horizonte, Brazil", lat: -19.9167, lon: -43.9345 },
  { name: "Buenos Aires, Argentina", lat: -34.6118, lon: -58.3960 },
  { name: "Córdoba, Argentina", lat: -31.4201, lon: -64.1888 },
  { name: "Rosario, Argentina", lat: -32.9468, lon: -60.6393 },
  { name: "Santiago, Chile", lat: -33.4489, lon: -70.6693 },
  { name: "Valparaíso, Chile", lat: -33.0472, lon: -71.6127 },
  { name: "Lima, Peru", lat: -12.0464, lon: -77.0428 },
  { name: "Arequipa, Peru", lat: -16.4090, lon: -71.5375 },
  { name: "Bogotá, Colombia", lat: 4.7110, lon: -74.0721 },
  { name: "Medellín, Colombia", lat: 6.2442, lon: -75.5812 },
  { name: "Cali, Colombia", lat: 3.4516, lon: -76.5320 },
  { name: "Caracas, Venezuela", lat: 10.4806, lon: -66.9036 },
  { name: "Maracaibo, Venezuela", lat: 10.6427, lon: -71.6125 }
];

// Timezone mapping
const cityTimezoneMap = {
  "New York, NY, USA": "America/New_York",
  "Los Angeles, CA, USA": "America/Los_Angeles",
  "Chicago, IL, USA": "America/Chicago",
  "Houston, TX, USA": "America/Chicago",
  "Phoenix, AZ, USA": "America/Phoenix",
  "Philadelphia, PA, USA": "America/New_York",
  "San Antonio, TX, USA": "America/Chicago",
  "San Diego, CA, USA": "America/Los_Angeles",
  "Dallas, TX, USA": "America/Chicago",
  "San Jose, CA, USA": "America/Los_Angeles",
  "London, UK": "Europe/London",
  "Paris, France": "Europe/Paris",
  "Berlin, Germany": "Europe/Berlin",
  "Madrid, Spain": "Europe/Madrid",
  "Rome, Italy": "Europe/Rome",
  "Amsterdam, Netherlands": "Europe/Amsterdam",
  "Vienna, Austria": "Europe/Vienna",
  "Prague, Czech Republic": "Europe/Prague",
  "Budapest, Hungary": "Europe/Budapest",
  "Warsaw, Poland": "Europe/Warsaw",
  "Moscow, Russia": "Europe/Moscow",
  "Tokyo, Japan": "Asia/Tokyo",
  "Seoul, South Korea": "Asia/Seoul",
  "Beijing, China": "Asia/Shanghai",
  "Shanghai, China": "Asia/Shanghai",
  "Hong Kong": "Asia/Hong_Kong",
  "Singapore": "Asia/Singapore",
  "Bangkok, Thailand": "Asia/Bangkok",
  "Jakarta, Indonesia": "Asia/Jakarta",
  "Kuala Lumpur, Malaysia": "Asia/Kuala_Lumpur",
  "Manila, Philippines": "Asia/Manila",
  "Ho Chi Minh City, Vietnam": "Asia/Ho_Chi_Minh",
  "Mumbai, India": "Asia/Kolkata",
  "Delhi, India": "Asia/Kolkata",
  "Bangalore, India": "Asia/Kolkata",
  "Chennai, India": "Asia/Kolkata",
  "Kolkata, India": "Asia/Kolkata",
  "Hyderabad, India": "Asia/Kolkata",
  "Pune, India": "Asia/Kolkata",
  "Ahmedabad, India": "Asia/Kolkata",
  "Sydney, Australia": "Australia/Sydney",
  "Melbourne, Australia": "Australia/Melbourne",
  "Brisbane, Australia": "Australia/Brisbane",
  "Perth, Australia": "Australia/Perth",
  "Adelaide, Australia": "Australia/Adelaide",
  "Toronto, Canada": "America/Toronto",
  "Montreal, Canada": "America/Montreal",
  "Vancouver, Canada": "America/Vancouver",
  "Calgary, Canada": "America/Edmonton",
  "Edmonton, Canada": "America/Edmonton",
  "Ottawa, Canada": "America/Toronto",
  "Mexico City, Mexico": "America/Mexico_City",
  "Guadalajara, Mexico": "America/Mexico_City",
  "Monterrey, Mexico": "America/Monterrey",
  "Puebla, Mexico": "America/Mexico_City",
  "São Paulo, Brazil": "America/Sao_Paulo",
  "Rio de Janeiro, Brazil": "America/Sao_Paulo",
  "Brasília, Brazil": "America/Sao_Paulo",
  "Salvador, Brazil": "America/Bahia",
  "Fortaleza, Brazil": "America/Fortaleza",
  "Belo Horizonte, Brazil": "America/Sao_Paulo",
  "Buenos Aires, Argentina": "America/Argentina/Buenos_Aires",
  "Córdoba, Argentina": "America/Argentina/Cordoba",
  "Rosario, Argentina": "America/Argentina/Buenos_Aires",
  "Santiago, Chile": "America/Santiago",
  "Valparaíso, Chile": "America/Santiago",
  "Lima, Peru": "America/Lima",
  "Arequipa, Peru": "America/Lima",
  "Bogotá, Colombia": "America/Bogota",
  "Medellín, Colombia": "America/Bogota",
  "Cali, Colombia": "America/Bogota",
  "Caracas, Venezuela": "America/Caracas",
  "Maracaibo, Venezuela": "America/Caracas"
};

const TransitForm = ({ 
  formData, 
  setFormData, 
  onSubmit, 
  onLoadSavedData, 
  onSaveBirthData, 
  savedBirthData, 
  selectedSavedData, 
  loading, 
  isMobile 
}) => {
  const [showSaveOption, setShowSaveOption] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [useCoordinates, setUseCoordinates] = useState(false);
  const [coordinateError, setCoordinateError] = useState('');

  // Create options from predefined cities
  useEffect(() => {
    const predefinedOptions = majorCities.map(city => ({
      value: city.name,
      label: city.name,
      lat: city.lat,
      lon: city.lon,
      timezone: cityTimezoneMap[city.name] || 'UTC',
      source: 'predefined'
    }));
    setLocationOptions(predefinedOptions);
  }, [formData.birthDate]);

  const handleChange = ({ target: { name, value } }) => {
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Check if all required fields are filled
      const allFieldsFilled = newData.birthDate && 
                             newData.birthTime && 
                             newData.birthPlace;
      setShowSaveOption(allFieldsFilled);
      
      return newData;
    });
  };

  const handleLocationChange = (selectedOption) => {
    setSelectedLocation(selectedOption);
    
    if (selectedOption) {
      const city = majorCities.find(c => c.name === selectedOption.value);
      if (city) {
        setFormData(prev => ({
          ...prev,
          birthPlace: selectedOption.value,
          latitude: city.lat.toString(),
          longitude: city.lon.toString(),
          timeZone: cityTimezoneMap[selectedOption.value] || 'UTC'
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        birthPlace: '',
        latitude: '',
        longitude: '',
        timeZone: ''
      }));
    }
  };

  const handleLocationSearch = useCallback(async (inputValue) => {
    if (!inputValue) return locationOptions;
    
    setIsLoadingOptions(true);
    
    try {
      // Filter predefined cities
      const filteredCities = majorCities.filter(city =>
        city.name.toLowerCase().includes(inputValue.toLowerCase())
      );
      
      const filteredOptions = filteredCities.map(city => ({
        value: city.name,
        label: city.name,
        lat: city.lat,
        lon: city.lon,
        timezone: cityTimezoneMap[city.name] || 'UTC',
        source: 'predefined'
      }));
      
      return filteredOptions;
    } catch (error) {
      console.error('Error searching locations:', error);
      return [];
    } finally {
      setIsLoadingOptions(false);
    }
  }, [locationOptions]);

  const handleCoordinateChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validate coordinates
    const lat = field === 'latitude' ? parseFloat(value) : parseFloat(formData.latitude);
    const lon = field === 'longitude' ? parseFloat(value) : parseFloat(formData.longitude);
    
    if (lat < -90 || lat > 90) {
      setCoordinateError('Latitude must be between -90 and 90');
    } else if (lon < -180 || lon > 180) {
      setCoordinateError('Longitude must be between -180 and 180');
    } else {
      setCoordinateError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.birthDate && formData.birthTime && formData.birthPlace) {
      onSubmit(formData);
    }
  };

  const handleSaveBirthData = () => {
    onSaveBirthData(formData);
  };

  // Custom components for AsyncSelect
  const CustomOption = ({ data, innerProps }) => (
    <div
      {...innerProps}
      style={{
        padding: '8px 12px',
        cursor: 'pointer',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        color: 'white',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}
    >
      <div style={{ fontWeight: '500' }}>{data.label}</div>
      <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
        {data.lat}, {data.lon}
      </div>
    </div>
  );

  const CustomSingleValue = ({ data }) => (
    <div style={{ color: 'white' }}>
      {data.label}
    </div>
  );

  const LoadingMessage = () => (
    <div style={{ padding: '8px 12px', color: 'rgba(255, 255, 255, 0.6)' }}>
      Searching...
    </div>
  );

  const NoOptionsMessage = () => (
    <div style={{ padding: '8px 12px', color: 'rgba(255, 255, 255, 0.6)' }}>
      No cities found
    </div>
  );

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '4px',
      minHeight: '40px',
      boxShadow: 'none',
      '&:hover': {
        border: '1px solid rgba(255, 255, 255, 0.5)'
      }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '4px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
      color: 'white',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)'
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'white'
    }),
    input: (provided) => ({
      ...provided,
      color: 'white'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.6)'
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.6)'
    }),
    loadingMessage: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.6)'
    })
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <form onSubmit={handleSubmit} className="ascendant-form" style={{ padding: '0.5rem', marginBottom: '0.4rem' }}>
        {/* Load Previous Birth Info - Comes first for users who already have saved data */}
        <div className="form-section" style={{ marginBottom: '0.5rem', padding: '0.6rem' }}>
          <div className="section-title">
            <User size={14} />
            <span style={{ fontSize: '0.85rem' }}>Load Previous Birth Info</span>
          </div>
          <div className="form-field">
            <select
              value={selectedSavedData?.id || ''}
              onChange={(e) => {
                const selected = savedBirthData.find(data => `${data.id}` === e.target.value || data.id === parseInt(e.target.value));
                if (selected) {
                  onLoadSavedData(selected);
                }
              }}
              style={{
                padding: '0 0.6rem',
                fontSize: '0.85rem',
                width: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                color: 'white'
              }}
            >
              <option value="">Select previously saved birth info...</option>
              {savedBirthData.map(data => (
                <option key={data.id} value={data.id}>
                  {data.birthDate} {data.birthTime} - {data.birthPlace}
                </option>
              ))}
            </select>
            <small style={{ marginTop: '0.5rem', color: 'rgba(255, 255, 255, 0.6)' }}>
              Load previously saved birth information
            </small>
          </div>
        </div>

        <div className="form-section" style={{ marginBottom: '0.5rem', padding: '0.6rem' }}>
          <div className="section-title">
            <User size={14} />
            <span style={{ fontSize: '0.85rem' }}>Name</span>
          </div>
          <div className="form-field">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              required
              style={{ 
                padding: '0 0.6rem', 
                fontSize: '0.85rem',
                width: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                color: 'white'
              }}
            />
          </div>
        </div>

        <div className="form-section" style={{ marginBottom: '0.5rem', padding: '0.6rem' }}>
          <div className="section-title">
            <Calendar size={14} />
            <span style={{ fontSize: '0.85rem' }}>Birth Details</span>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label style={{ fontSize: '0.75rem' }}>Birth Date</label>
              <input
                type="date"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleChange}
                required
                style={{ 
                  padding: '0 0.6rem', 
                  fontSize: '0.85rem',
                  width: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '4px',
                  color: 'white'
                }}
              />
            </div>
            <div className="form-field">
              <label style={{ fontSize: '0.75rem' }}>Birth Time</label>
              <input
                type="time"
                name="birthTime"
                value={formData.birthTime}
                onChange={handleChange}
                required
                style={{ 
                  padding: '0 0.6rem', 
                  fontSize: '0.85rem',
                  width: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '4px',
                  color: 'white'
                }}
              />
            </div>
          </div>
        </div>

        <div className="form-section" style={{ marginBottom: '0.5rem', padding: '0.6rem' }}>
          <div className="section-title">
            <MapPin size={14} />
            <span style={{ fontSize: '0.85rem' }}>Birth Location</span>
          </div>
          
          {!useCoordinates ? (
            <div className="form-field">
              <AsyncSelect
                value={selectedLocation}
                onChange={handleLocationChange}
                loadOptions={handleLocationSearch}
                placeholder="Select your city from the list or start typing to search..."
                isClearable
                isSearchable
                isLoading={isLoadingOptions}
                loadingMessage={LoadingMessage}
                noOptionsMessage={NoOptionsMessage}
                components={{
                  Option: CustomOption,
                  SingleValue: CustomSingleValue
                }}
                styles={customSelectStyles}
                cacheOptions={false}
                defaultOptions={locationOptions}
              />
              <small style={{ marginTop: '0.5rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                Search from 200+ major cities or enter any location worldwide
              </small>
            </div>
          ) : (
            <div className="form-field">
              <div className="form-row">
                <div className="form-field">
                  <label>Latitude</label>
                  <input
                    type="number"
                    step="0.000001"
                    name="latitude"
                    value={formData.latitude}
                    onChange={(e) => handleCoordinateChange('latitude', e.target.value)}
                    placeholder="e.g., 40.7128"
                    required
                    style={{ 
                      padding: '0 0.6rem', 
                      fontSize: '0.85rem',
                      width: '100%',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '4px',
                      color: 'white'
                    }}
                  />
                </div>
                <div className="form-field">
                  <label>Longitude</label>
                  <input
                    type="number"
                    step="0.000001"
                    name="longitude"
                    value={formData.longitude}
                    onChange={(e) => handleCoordinateChange('longitude', e.target.value)}
                    placeholder="e.g., -74.0060"
                    required
                    style={{ 
                      padding: '0 0.6rem', 
                      fontSize: '0.85rem',
                      width: '100%',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '4px',
                      color: 'white'
                    }}
                  />
                </div>
              </div>
              {coordinateError && (
                <div style={{ color: '#ff4757', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  {coordinateError}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: '0.75rem' }}>
            <button
              type="button"
              onClick={() => setUseCoordinates(!useCoordinates)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                padding: '0.35rem 0.6rem',
                color: 'white',
                fontSize: '0.8rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}
            >
              <Info size={12} />
              {useCoordinates ? 'Use City Search' : 'Use Coordinates'}
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          flexDirection: isMobile ? 'column' : 'row',
          marginTop: '1rem'
        }}>
          <button
            type="submit"
            disabled={loading || !formData.birthDate || !formData.birthTime || !formData.birthPlace}
            style={{
              flex: 1,
              padding: '1rem',
              backgroundColor: '#FFD700',
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Calculating Transits...
              </>
            ) : (
              <>
                <ArrowRight size={20} />
                Calculate Transits
              </>
            )}
          </button>

          {showSaveOption && (
            <button
              type="button"
              onClick={handleSaveBirthData}
              style={{
                padding: '1rem',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Save size={20} />
              Save Birth Info
            </button>
          )}
        </div>
      </form>
    </motion.div>
  );
};

export default TransitForm;
